import React, { useEffect, useState } from "react";
import { CButton, CFormInput, CFormLabel, CFormSelect } from "@coreui/react";
import { CMultiSelect } from "@tradingdroid/coreui-pro";
import isEqual from "lodash-es/isEqual";
import {
  JOB_STATUS_NAMES,
  JOB_TYPE_NAMES,
  type JobStatusName,
} from "@tradingdroid/frontends-types";

import type { JobFilters } from "@src/types/filters";

interface Option {
  value: number | string;
  label: string;
  custom?: boolean;
  disabled?: boolean;
  selected?: boolean;
}

interface Props {
  appliedFilters: JobFilters; // Current applied filters
  onApplyFilters: (filters: JobFilters) => void;
  onClearFilters: () => void;
  disabled?: boolean; // Disable all controls during loading
}

// Create options for CMultiSelect
const JOB_TYPE_OPTIONS: Option[] = JOB_TYPE_NAMES.map((type) => ({
  value: type,
  label: type.toUpperCase(),
}));

// Convert applied filters to local state
const getDraftState = (applied: JobFilters) => ({
  hiddenJobTypes: [...applied.hiddenJobTypes],
  status: applied.status,
  dateFrom: applied.dateRange?.from || "",
  dateTo: applied.dateRange?.to || "",
  dateField: applied.dateField,
  workerId: applied.workerId || "",
});

export const FilterJobsControls: React.FC<Props> = ({
  appliedFilters,
  onApplyFilters,
  onClearFilters,
  disabled = false,
}) => {
  const [draftState, setDraftState] = useState(() => getDraftState(appliedFilters));

  // Update draft state when applied filters change (e.g., page navigation)
  useEffect(() => {
    setDraftState(getDraftState(appliedFilters));
  }, [appliedFilters]);

  // Use draft state's hiddenTypeValues (already an array)
  const hiddenTypeValues = draftState.hiddenJobTypes;

  // Check if draft state differs from applied state (for dirty indicator)
  const isDirty =
    // Hidden types changed (use sorted comparison for order-independent check)
    !isEqual([...draftState.hiddenJobTypes].sort(), [...appliedFilters.hiddenJobTypes].sort()) ||
    // Other primitive fields
    draftState.status !== appliedFilters.status ||
    draftState.dateFrom !== (appliedFilters.dateRange?.from || "") ||
    draftState.dateTo !== (appliedFilters.dateRange?.to || "") ||
    draftState.dateField !== appliedFilters.dateField ||
    draftState.workerId !== (appliedFilters.workerId || "");

  const handleApply = () => {
    const newFilters: JobFilters = {
      hiddenJobTypes: [...draftState.hiddenJobTypes],
      status: draftState.status,
      dateRange: {
        from: draftState.dateFrom || null,
        to: draftState.dateTo || null,
      },
      dateField: draftState.dateField,
      workerId: draftState.workerId || null,
    };
    onApplyFilters(newFilters);
  };

  const handleClear = () => {
    onClearFilters();
  };

  const handleTypeChange = (selected: Option[]) => {
    const newHiddenTypes = selected.map((opt) => String(opt.value));
    setDraftState((prev) => ({ ...prev, hiddenJobTypes: newHiddenTypes }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value === "" ? null : e.target.value;
    setDraftState((prev) => ({ ...prev, status: newStatus }));
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFrom = e.target.value;
    setDraftState((prev) => ({ ...prev, dateFrom: newFrom }));
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTo = e.target.value;
    setDraftState((prev) => ({ ...prev, dateTo: newTo }));
  };

  const handleDateFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newField = e.target.value as "start_ts" | "complete_ts";
    setDraftState((prev) => ({ ...prev, dateField: newField }));
  };

  const handleWorkerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWorkerId = e.target.value;
    setDraftState((prev) => ({ ...prev, workerId: newWorkerId }));
  };

  return (
    <div className="FilterJobsControls mb-3">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        {/* Job Type Multi-Select (Hidden Types) */}
        <div className="d-flex align-items-center">
          <CFormLabel className="mb-0 me-2">Hide:</CFormLabel>
          <CMultiSelect
            options={JOB_TYPE_OPTIONS}
            value={hiddenTypeValues}
            onChange={handleTypeChange}
            multiple
            placeholder="Select job types to hide..."
            cleaner
            size="sm"
            style={{ minWidth: "250px" }}
            disabled={disabled}
          />
        </div>

        {/* Status Selector */}
        <div className="d-flex align-items-center">
          <CFormLabel htmlFor="statusSelect" className="mb-0 me-2">
            Status:
          </CFormLabel>
          <CFormSelect
            id="statusSelect"
            size="sm"
            value={draftState.status || ""}
            onChange={handleStatusChange}
            style={{ width: "140px" }}
            disabled={disabled}
          >
            <option value="">All</option>
            {JOB_STATUS_NAMES.map((s: JobStatusName) => (
              <option key={s} value={s}>
                {s.toUpperCase()}
              </option>
            ))}
          </CFormSelect>
        </div>

        {/* Worker ID Input */}
        <div className="d-flex align-items-center">
          <CFormLabel htmlFor="workerIdInput" className="mb-0 me-2">
            Worker ID:
          </CFormLabel>
          <CFormInput
            type="text"
            id="workerIdInput"
            size="sm"
            value={draftState.workerId}
            onChange={handleWorkerIdChange}
            placeholder="Filter by worker..."
            style={{ width: "140px" }}
            disabled={disabled}
          />
        </div>

        {/* Date Range Selector with Field Select */}
        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
          <CFormSelect
            id="dateFieldSelect"
            size="sm"
            value={draftState.dateField}
            onChange={handleDateFieldChange}
            style={{ width: "80px" }}
            disabled={disabled}
          >
            <option value="start_ts">From</option>
            <option value="complete_ts">To</option>
          </CFormSelect>

          <div className="d-flex align-items-center gap-2">
            <CFormInput
              type="date"
              id="dateFrom"
              size="sm"
              value={draftState.dateFrom}
              onChange={handleDateFromChange}
              style={{ width: "140px" }}
              disabled={disabled}
            />

            <CFormLabel htmlFor="dateTo" className="mb-0">
              -
            </CFormLabel>

            <CFormInput
              type="date"
              id="dateTo"
              size="sm"
              value={draftState.dateTo}
              onChange={handleDateToChange}
              style={{ width: "140px" }}
              disabled={disabled}
            />
          </div>
        </div>

        <CButton
          size="sm"
          color={isDirty ? "primary" : "secondary"}
          className="ms-auto"
          variant={isDirty ? undefined : "outline"}
          onClick={handleApply}
          disabled={disabled || !isDirty}
        >
          Filter
        </CButton>

        <CButton
          size="sm"
          color="secondary"
          variant="outline"
          onClick={handleClear}
          disabled={disabled}
        >
          Clear
        </CButton>
      </div>
    </div>
  );
};
